import {
  type ReleaseSummaryWorkflow,
  type WorkflowStep,
  type WorkflowStepInput,
  WorkflowStepStatuses,
} from "~/api/types";

import { dateDiff } from "./date";
import { currentUserAtTopCompareFn, printFullName } from "./user";

const toMidnight = (date: Date | string) => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value.getTime();
};

export type WorkflowDateKey = number | "reviewFrom" | "reviewTo";

export function buildWorkflowDateChain(
  steps: { dueAt: Date | string | null | undefined }[],
  reviewFrom: Date | string | null | undefined,
  reviewTo: Date | string | null | undefined,
): { key: WorkflowDateKey; date: Date | string | null | undefined }[] {
  return [
    { key: "reviewFrom", date: reviewFrom },
    ...steps.map((step, index) => ({ key: index, date: step.dueAt })),
    { key: "reviewTo", date: reviewTo },
  ];
}

export type StepDateIssue = "order" | "afterReviewTo";

export const isBeforeToday = (date: Date | string | null | undefined) =>
  dateDiff(date, new Date(), "day") < 0;

export function getStepDateIssues(
  chain: { key: WorkflowDateKey; date: Date | string | null | undefined }[],
): Map<number, StepDateIssue> {
  const outOfOrder = findOutOfOrderDates<WorkflowDateKey>(chain);
  const issues = new Map<number, StepDateIssue>();

  for (const key of outOfOrder) {
    if (typeof key === "number") {
      issues.set(key, "order");
    }
  }

  if (outOfOrder.has("reviewTo")) {
    const lastDatedStep = chain.reduce<number>(
      (last, entry) =>
        typeof entry.key === "number" && entry.date ? entry.key : last,
      -1,
    );
    if (lastDatedStep >= 0 && !issues.has(lastDatedStep)) {
      issues.set(lastDatedStep, "afterReviewTo");
    }
  }

  return issues;
}

export interface DateChainBounds {
  minDueAt?: string;
  maxDueAt?: string;
}

/**
 * Bounds each entry by its dated neighbours in the chain, so a date can only be
 * picked inside the window its surroundings leave open. Undated entries are
 * transparent: the bound falls through to the nearest dated one on that side.
 *
 * ISO strings rather than Dates so the bounds compare by value and keep the
 * memoised cells that consume them from re-rendering.
 */
export function getDateChainBounds<Key>(
  entries: { key: Key; date: Date | string | null | undefined }[],
): Map<Key, DateChainBounds> {
  const toIso = (date: Date | string) => new Date(date).toISOString();
  const bounds = new Map<Key, DateChainBounds>();

  let preceding: string | undefined;
  for (const entry of entries) {
    bounds.set(entry.key, { minDueAt: preceding });
    if (entry.date) preceding = toIso(entry.date);
  }

  let following: string | undefined;
  for (let i = entries.length - 1; i >= 0; i--) {
    const entry = entries[i]!;
    const entryBounds = bounds.get(entry.key);
    if (entryBounds) entryBounds.maxDueAt = following;
    if (entry.date) following = toIso(entry.date);
  }

  return bounds;
}

export function findOutOfOrderDates<Key>(
  entries: { key: Key; date: Date | string | null | undefined }[],
): Set<Key> {
  const outOfOrder = new Set<Key>();
  let previous: number | undefined;

  for (const entry of entries) {
    if (!entry.date) continue;

    const current = toMidnight(entry.date);
    if (previous !== undefined && current < previous) {
      outOfOrder.add(entry.key);
    }
    previous = current;
  }

  return outOfOrder;
}

export const progressRatio = ({
  completedSteps,
  totalSteps,
}: ReleaseSummaryWorkflow) =>
  totalSteps === 0 ? 0 : completedSteps / totalSteps;

export function mapStepInputsToWorkflowSteps(
  steps: WorkflowStepInput[],
): WorkflowStep[] {
  return steps.map((s, i) => ({
    id: s.id ?? -(i + 1),
    name: s.name,
    description: s.description ?? "",
    type: s.type,
    dueAt: s.dueAt,
    order: i,
    status: WorkflowStepStatuses.upcoming,
    users: s.users,
  }));
}

interface ExtractOwnerInput {
  userId: number;
  firstName: string;
  lastName?: string;
}

export function extractOwners(
  docs: ExtractOwnerInput[],
  currentUserId: number,
) {
  const ownerIds = new Set<number>();

  return docs
    .filter(({ userId }) => {
      if (ownerIds.has(userId)) {
        return false;
      } else {
        ownerIds.add(userId);
        return true;
      }
    })
    .toSorted((a, b) =>
      currentUserAtTopCompareFn(a.userId, b.userId, currentUserId),
    )
    .map((owner) => ({
      label: printFullName(owner, owner.userId === currentUserId),
      value: `${owner.userId}`,
    }));
}
